// Invoice looks: templates, typefaces and brand colours. A port of
// invoice_template.dart, invoice_font.dart and the Customize choices.

/** Free: Classic, Bold, Minimal, Modern. Pro adds Elegant and Sidebar. */
export const TEMPLATES = [
  { id: 'classic', isPro: false },
  { id: 'bold', isPro: false },
  { id: 'minimal', isPro: false },
  { id: 'modern', isPro: false },
  { id: 'elegant', isPro: true },
  { id: 'sidebar', isPro: true },
] as const;

export type TemplateId = (typeof TEMPLATES)[number]['id'];

export function templateOf(id: string): (typeof TEMPLATES)[number] {
  return TEMPLATES.find((t) => t.id === id) ?? TEMPLATES[0];
}

/**
 * The typefaces an invoice can be set in. Files are the phone app's
 * (packages/paidlark_core/assets/fonts), so invoices look the same on both.
 */
export const FONTS = [
  { id: 'jakarta', label: 'Plus Jakarta Sans', file: 'plus-jakarta-sans', heavy: 800, display: 'fraunces', displayWeight: 600 },
  { id: 'poppins', label: 'Poppins', file: 'poppins', heavy: 800, display: null, displayWeight: null },
  { id: 'inter', label: 'Inter', file: 'inter', heavy: 800, display: null, displayWeight: null },
  { id: 'dm', label: 'DM Sans & DM Serif', file: 'dm-sans', heavy: 800, display: 'dm-serif-display', displayWeight: 400 },
  { id: 'space-grotesk', label: 'Space Grotesk', file: 'space-grotesk', heavy: 700, display: null, displayWeight: null },
  { id: 'lora', label: 'Lora', file: 'lora', heavy: 700, display: null, displayWeight: null },
] as const;

export type InvoiceFont = (typeof FONTS)[number];

export function fontOf(id: string): InvoiceFont {
  return FONTS.find((f) => f.id === id) ?? FONTS[0];
}

/** The brand colours offered first; any other comes from the colour input. */
export const BRAND_COLOURS = [0xff0e5e4e, 0xffff7a59, 0xff3d5afe, 0xff7c3aed, 0xff12171a, 0xffc2185b];

/** `#0E5E4E` from an ARGB number. */
export function argbToHex(argb: number): string {
  return '#' + (argb & 0xffffff).toString(16).padStart(6, '0').toUpperCase();
}

/** An opaque ARGB number from `#0E5E4E`; null when it isn't a colour. */
export function hexToArgb(hex: string): number | null {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  return match ? (0xff000000 | parseInt(match[1], 16)) >>> 0 : null;
}

/** Text on the brand colour: white, or ink on a light colour. */
export function onBrand(argb: number): string {
  const r = ((argb >> 16) & 0xff) / 255;
  const g = ((argb >> 8) & 0xff) / 255;
  const b = (argb & 0xff) / 255;
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  const luminance = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return luminance > 0.6 ? '#12171A' : '#FFFFFF';
}
